import type { ButtonHTMLAttributes, Ref } from 'react'

import { cn } from '@/ui/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'destructive'
export type ButtonSize = 'default' | 'sm'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  ref?: Ref<HTMLButtonElement>
}

/**
 * Station Kit's plain dossier button (spec 007 PR1, disabled treatment
 * corrected by spec 010) — replaces the retired shadcn `Button` primitive. No
 * radius, no shadow, no ring: just a hairline or solid-fill rectangle with an
 * Archivo Narrow uppercase, tracked label. Token-driven only, so it reads
 * correctly whether it sits on a `--paper` or `--ink` surface — `secondary`'s
 * border/text use `currentColor` (inherited from whichever surface renders it)
 * rather than a hardcoded ink/paper choice.
 *
 * Variants:
 * - `primary` — solid `--accent-fill` with `--paper` text; reads on either
 *   surface. Hover brightens the fill (`brightness(1.12)`).
 * - `secondary` — transparent, hairline `currentColor` border. Hover washes
 *   in the context accent (`--accent-wash`).
 * - `destructive` — solid `--alert` red with `--paper` text. Hover brightens
 *   like `primary`.
 *
 * Disabled is a real fourth *state*, not a washed fill: a disabled button
 * drops any variant fill for a transparent box with a hairline `--ink-25`
 * border, `--ink-25` text and `cursor:not-allowed` (spec 010 §6 — the earlier
 * `opacity-50` made disabled and destructive both read as faded pink, so
 * nothing looked unavailable and nothing looked dangerous). The `enabled:`
 * hover guards keep the wash/brighten off a disabled control. Because the
 * `disabled:` utilities carry the `:disabled` pseudo-class they out-specify the
 * variant fill/border/text at runtime — the primary "stays in the disabled
 * treatment until valid" pattern (spec 010 §5 forms) is just `<Button disabled>`.
 *
 * Geometry follows the six-size type scale, retuned by spec 014 (Station Kit
 * v3, task 1a) so the label never sits within 13px of a border: the default
 * is `10px 18px` with a `38px` min-height at `11px/.2em`, and `size="sm"` is
 * `8px 13px` with a `32px` min-height at `10px/.18em` for row-level and
 * confirm controls.
 */
export function Button({ variant = 'primary', size = 'default', className, ref, ...props }: ButtonProps) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex min-h-[38px] shrink-0 items-center justify-center gap-2 border px-[18px] py-[10px] font-sans text-[11px] font-semibold tracking-[0.2em] uppercase transition-[filter,background-color] disabled:cursor-not-allowed disabled:border-ink-25 disabled:bg-transparent disabled:text-ink-25',
        variant === 'primary' && 'border-transparent bg-accent-fill text-paper enabled:hover:brightness-[1.12]',
        variant === 'secondary' && 'border-current/25 bg-transparent text-current enabled:hover:bg-accent-wash',
        variant === 'destructive' && 'border-transparent bg-alert text-paper enabled:hover:brightness-[1.12]',
        size === 'sm' && 'min-h-[32px] px-[13px] py-[8px] text-[10px] tracking-[0.18em]',
        className,
      )}
      {...props}
    />
  )
}
